
import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import ErrorBoundary from "@/components/ErrorBoundary";

// Loading component for Suspense
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Lazy load all page components
const Index = lazy(() => import("@/pages/Index"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const CancellationGuide = lazy(() => import("@/pages/CancellationGuide"));
const AccessibilityStatement = lazy(() => import("@/pages/AccessibilityStatement"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const SignUp = lazy(() => import("@/pages/SignUp"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const InvestorMatchingQuiz = lazy(() => import("@/pages/InvestorMatchingQuiz"));
const PitchAnalyzer = lazy(() => import("@/pages/PitchAnalyzer"));
const PitchDeckGenerator = lazy(() => import("@/pages/PitchDeckGenerator"));
const FundraisingTimeline = lazy(() => import("@/pages/FundraisingTimeline"));
const FundraisingVideos = lazy(() => import("@/pages/FundraisingVideos"));
const FundraisingStats = lazy(() => import("@/pages/FundraisingStats"));
const MasteringFundraising = lazy(() => import("@/pages/MasteringFundraising"));
const FinancialModeling = lazy(() => import("@/pages/FinancialModeling"));
const MAStrategies = lazy(() => import("@/pages/MAStrategies"));
const ValuationCalculator = lazy(() => import("@/pages/ValuationCalculator"));
const DealRoom = lazy(() => import("@/pages/DealRoom"));
const InvestorDirectory = lazy(() => import("@/pages/InvestorDirectory"));
const InvestorIntelligence = lazy(() => import("@/pages/InvestorIntelligence"));
const InvestorCrm = lazy(() => import("@/pages/InvestorCrm"));
const AiAdvisor = lazy(() => import("@/pages/AiAdvisor"));
const ManageSubscription = lazy(() => import("@/pages/ManageSubscription"));
const Settings = lazy(() => import("@/pages/Settings"));
const GetHelp = lazy(() => import("@/pages/GetHelp"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Templates = lazy(() => import("@/pages/Templates"));
const Company = lazy(() => import("@/pages/Company"));
const Pathways = lazy(() => import("@/pages/Pathways"));
const FinancialModelTemplate = lazy(() => import("@/pages/FinancialModelTemplate"));
const DilutionSimulator = lazy(() => import("@/pages/DilutionSimulator"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const LeadCapture = lazy(() => import("@/pages/LeadCapture"));
const PitchGuide = lazy(() => import("@/pages/PitchGuide"));
const BackupManager = lazy(() => import("@/pages/BackupManager"));

// Helper to wrap components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<PageLoadingFallback />}>
    <Component />
  </Suspense>
);

// Define interfaces for route objects
export interface RouteBase {
  element: React.ReactNode;
  noFooter?: boolean;
}

export interface RouteWithPath extends RouteBase {
  path: string;
}

// Define public routes that don't need any protection
export const publicRoutes: RouteWithPath[] = [
  { path: "/", element: withSuspense(Index) },
  { path: "/pricing", element: withSuspense(Pricing) },
  { path: "/cookie-policy", element: withSuspense(CookiePolicy) },
  { path: "/terms-and-conditions", element: withSuspense(TermsAndConditions) },
  { path: "/disclaimer", element: withSuspense(Disclaimer) },
  { path: "/cancellation-guide", element: withSuspense(CancellationGuide) },
  { path: "/accessibility-statement", element: withSuspense(AccessibilityStatement) },
  { path: "/privacy-policy", element: withSuspense(PrivacyPolicy), noFooter: true },
  { path: "/signin", element: withSuspense(SignIn) },
  { path: "/signup", element: withSuspense(SignUp) },
  { path: "/subscription-success", element: withSuspense(SubscriptionSuccess) },
  { path: "/lead-capture", element: withSuspense(LeadCapture) },
  { path: "/pitch-guide", element: withSuspense(PitchGuide) },
  { path: "*", element: withSuspense(NotFound) }
];

// Define dashboard routes that need onboarding protection
export const dashboardRoutes: RouteWithPath[] = [
  { path: "/dashboard", element: withSuspense(Dashboard) },
  { path: "/investor-matching-quiz", element: withSuspense(InvestorMatchingQuiz) },
  { path: "/pitch-analyzer", element: withSuspense(PitchAnalyzer) },
  { path: "/pitch-deck-generator", element: withSuspense(PitchDeckGenerator) },
  { path: "/fundraising-timeline", element: withSuspense(FundraisingTimeline) },
  { path: "/fundraising-videos", element: withSuspense(FundraisingVideos) },
  { path: "/fundraising-stats", element: withSuspense(FundraisingStats) },
  { path: "/mastering-fundraising", element: withSuspense(MasteringFundraising) },
  { path: "/financial-modeling", element: withSuspense(FinancialModeling) },
  { path: "/ma-strategies", element: withSuspense(MAStrategies) },
  { path: "/valuation-calculator", element: withSuspense(ValuationCalculator) },
  { path: "/deal-room", element: withSuspense(DealRoom) },
  { path: "/investor-directory", element: withSuspense(InvestorDirectory) },
  { path: "/investor-intelligence", element: withSuspense(InvestorIntelligence) },
  { path: "/investor-crm", element: withSuspense(InvestorCrm) },
  { path: "/ai-coaching", element: withSuspense(AiAdvisor) },
  { path: "/manage-subscription", element: withSuspense(ManageSubscription) },
  { path: "/settings", element: withSuspense(Settings) },
  { path: "/get-help", element: withSuspense(GetHelp) },
  { path: "/feedback", element: withSuspense(Feedback) },
  { path: "/templates", element: withSuspense(Templates) },
  { path: "/pathways", element: withSuspense(Pathways) },
  { path: "/financial-model-template", element: withSuspense(FinancialModelTemplate) },
  { path: "/dilution-simulator", element: withSuspense(DilutionSimulator) },
  { path: "/company", element: withSuspense(Company) },
  { path: "/admin-dashboard", element: withSuspense(AdminDashboard) },
  { path: "/backup-manager", element: withSuspense(BackupManager) }
];

// Wrap each dashboard route with error boundary
export const wrappedDashboardRoutes = dashboardRoutes.map(route => ({
  ...route,
  element: <ErrorBoundary>{route.element}</ErrorBoundary>
}));
